import { Download, FileText, Plus, Trash2 } from 'lucide-react';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/utils/format';

import type { ReportRecord, ReportDataEntry } from '@repo/shared-types';

function getMetricValue(report: ReportRecord, key: string): number | null {
  const entry: ReportDataEntry | undefined = report.data.find((d) => d.key === key);
  if (!entry) return null;
  return entry.value;
}

interface ReportsTableProps {
  reports: ReportRecord[];
  isLoading: boolean;
  pagination?: { page: number; totalPages: number; total: number; limit: number };
  onPageChange: (page: number) => void;
  onCreateClick: () => void;
  onExportPDF: (report: ReportRecord) => void;
  onDelete: (report: ReportRecord) => void;
}

export function ReportsTable({
  reports,
  isLoading,
  pagination,
  onPageChange,
  onCreateClick,
  onExportPDF,
  onDelete,
}: ReportsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="py-12 text-center">
        <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">No reports</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by creating a new report.
        </p>
        <div className="mt-6">
          <Button onClick={onCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            New Report
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left">
              <th className="border-b px-3 py-2">Report Name</th>
              <th className="border-b px-3 py-2">Campaign</th>
              <th className="border-b px-3 py-2">Date Range</th>
              <th className="border-b px-3 py-2">Total Calls</th>
              <th className="border-b px-3 py-2">Created</th>
              <th className="border-b px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report: ReportRecord) => (
              <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                <td className="border-b px-3 py-2 font-medium">
                  {report.name}
                  {(getMetricValue(report, 'total_hours_from_lead_generation_email') != null ||
                    getMetricValue(report, 'total_hours_from_newcross_callbacks') != null) && (
                    <div className="mt-1 space-y-0.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {getMetricValue(report, 'total_hours_from_lead_generation_email') != null && (
                        <div>
                          Lead Gen:{' '}
                          {getMetricValue(report, 'total_hours_from_lead_generation_email')}h
                        </div>
                      )}
                      {getMetricValue(report, 'total_hours_from_newcross_callbacks') != null && (
                        <div>
                          Newcross: {getMetricValue(report, 'total_hours_from_newcross_callbacks')}h
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="border-b px-3 py-2">{report.campaign_name || '-'}</td>
                <td className="border-b px-3 py-2">
                  {report.campaign_name
                    ? '-'
                    : `${formatDate(report.week_start_date)} - ${formatDate(report.week_end_date)}`}
                </td>
                <td className="border-b px-3 py-2">
                  {(getMetricValue(report, 'total_outbound_calls') ?? 0) +
                    (getMetricValue(report, 'total_inbound_calls') ?? 0)}
                </td>
                <td className="border-b px-3 py-2">{formatDate(report.created_at)}</td>
                <td className="border-b px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onExportPDF(report)}>
                      <Download className="mr-1 h-4 w-4" />
                      PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDelete(report)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={onPageChange}
            resourceName="reports"
          />
        </div>
      )}
    </>
  );
}
